/**
 * Semantic Similarity Service
 * Checks for semantically similar items using sentence transformers
 */

export interface SimilarItem {
  item: any
  similarity_score: number
  similarity_category: 'identical' | 'very_similar' | 'similar' | 'somewhat_similar' | 'different'
}

export interface SimilarityCheckRequest {
  title: string
  description?: string
  hierarchy_type: string
  existing_items: any[]
  threshold?: number
}

export interface SimilarityCheckResponse {
  similar_items: SimilarItem[]
  has_similar: boolean
  threshold_used: number
}

export class SimilarityService {
  private baseUrl = '/horton/check-similar-items'

  async checkSimilarItems(request: SimilarityCheckRequest): Promise<SimilarityCheckResponse> {
    try {
      const response = await fetch(this.baseUrl, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const data = await response.json()
      return data
    } catch (error) {
      console.error('Error checking similar items:', error)
      throw error
    }
  }

  getCategoryLabel(category: string): string {
    const labels: Record<string, string> = {
      'identical': 'Identical',
      'very_similar': 'Very Similar',
      'similar': 'Similar',
      'somewhat_similar': 'Somewhat Similar',
      'different': 'Different'
    }
    return labels[category] || category
  }

  getCategoryColor(category: string): string {
    const colors: Record<string, string> = {
      'identical': 'text-bad',
      'very_similar': 'text-warn',
      'similar': 'text-warn',
      'somewhat_similar': 'text-brand',
      'different': 'text-secondary'
    }
    return colors[category] || 'text-secondary'
  }

  getCategoryIcon(category: string): string {
    const icons: Record<string, string> = {
      'identical': '🔴',
      'very_similar': '🟠',
      'similar': '🟡',
      'somewhat_similar': '🔵',
      'different': '⚪'
    }
    return icons[category] || '⚪'
  }
}

export const similarityService = new SimilarityService()

